import React, { useState, useEffect, useMemo } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Alert,
    Snackbar,
    Card,
    CardMedia,
    Chip,
    FormHelperText,
    Divider,
    Stack
} from '@mui/material';
import {
    Close as CloseIcon,
    CloudUpload as UploadIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { createProduct, updateProduct } from '../Service/productServices';

const INITIAL_FORM_DATA = {
    name: '',
    description: '',
    price: '',
    stock: '',
    uom: 'Tablet',
    dosage: '',
    expiryDate: '',
    status: 'Active',
    imageUrl: '',
    category: '',
    cost: '',
};

const UOM_OPTIONS = [
    { value: 'Tablet', label: 'Tablet' },
    { value: 'Capsule', label: 'Capsule' },
    { value: 'Syrup', label: 'Syrup (ml)' },
    { value: 'Injection', label: 'Injection' },
    { value: 'Cream', label: 'Cream (g)' },
    { value: 'Drops', label: 'Drops' },
];

const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active', color: 'success' },
    { value: 'Inactive', label: 'Inactive', color: 'default' },
    { value: 'Discontinued', label: 'Discontinued', color: 'error' },
];

const AddMedicineDrawer = ({ open, onClose, selectedMedicine, onMedicineAdded, embedded = false }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const isEditing = useMemo(() => Boolean(selectedMedicine?._id || selectedMedicine?.id), [selectedMedicine]);

    useEffect(() => {
        if (open) {
            if (selectedMedicine) {
                setFormData({
                    name: selectedMedicine.name || '',
                    description: selectedMedicine.description || '',
                    price: selectedMedicine.price?.toString() || '',
                    stock: selectedMedicine.stock?.toString() || '',
                    uom: selectedMedicine.uom || 'Tablet',
                    dosage: selectedMedicine.specifications?.dosage || '',
                    expiryDate: selectedMedicine.specifications?.expiryDate?.split('T')[0] || '',
                    status: selectedMedicine.status || 'Active',
                    imageUrl: selectedMedicine.imageUrl || '',
                    category: selectedMedicine.category || '',
                    cost: selectedMedicine.cost?.toString() || '',
                });
            } else {
                setFormData(INITIAL_FORM_DATA);
            }
            setErrors({});
        }
    }, [open, selectedMedicine]);

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'name':
                if (!value.trim()) {
                    newErrors.name = 'Name is required';
                } else if (value.length < 2) {
                    newErrors.name = 'Name must be at least 2 characters';
                } else {
                    delete newErrors.name;
                }
                break;

            case 'price':
                if (!value) {
                    newErrors.price = 'Price is required';
                } else if (parseFloat(value) <= 0) {
                    newErrors.price = 'Price must be greater than 0';
                } else {
                    delete newErrors.price;
                }
                break;

            case 'stock':
                if (!value) {
                    newErrors.stock = 'Stock is required';
                } else if (parseInt(value) < 0) {
                    newErrors.stock = 'Stock cannot be negative';
                } else {
                    delete newErrors.stock;
                }
                break;

            case 'cost':
                if (value && parseFloat(value) < 0) {
                    newErrors.cost = 'Cost cannot be negative';
                } else {
                    delete newErrors.cost;
                }
                break;

            case 'expiryDate':
                if (value) {
                    const expiryDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (expiryDate <= today) {
                        newErrors.expiryDate = 'Expiry date must be in the future';
                    } else {
                        delete newErrors.expiryDate;
                    }
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        validateField(name, value);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showNotification('Please select a valid image file', 'error');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            showNotification('Image must not exceed 5MB', 'error');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData(prev => ({ ...prev, imageUrl: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, imageUrl: '' }));
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.price || parseFloat(formData.price) <= 0) {
            newErrors.price = 'Price is required and must be greater than 0';
        }
        if (!formData.stock || parseInt(formData.stock) < 0) {
            newErrors.stock = 'Stock is required and cannot be negative';
        }
        if (formData.cost && parseFloat(formData.cost) < 0) {
            newErrors.cost = 'Cost cannot be negative';
        }
        
        if (formData.expiryDate) {
            const expiryDate = new Date(formData.expiryDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (expiryDate <= today) {
                newErrors.expiryDate = 'Expiry date must be in the future';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showNotification('Please correct the errors in the form', 'error');
            return;
        }

        setLoading(true);
        try {
            const medicineData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                type: 'consumible',
                price: parseFloat(formData.price),
                stock: parseInt(formData.stock),
                cost: formData.cost ? parseFloat(formData.cost) : undefined,
                uom: formData.uom,
                category: formData.category.trim(),
                status: formData.status,
                isActive: formData.status === 'Active',
                imageUrl: formData.imageUrl,
                specifications: {
                    dosage: formData.dosage.trim(),
                    expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null
                }
            };

            let result;
            if (isEditing) {
                const id = selectedMedicine._id || selectedMedicine.id;
                result = await updateProduct(id, medicineData);
            } else {
                result = await createProduct(medicineData);
            }

            if (result) {
                onMedicineAdded(result);
                showNotification(
                    isEditing ? 'Medicine updated successfully' : 'Medicine created successfully',
                    'success'
                );
                setTimeout(() => onClose(), 1500);
            }
        } catch (error) {
            console.error('Error saving medicine:', error);
            showNotification('Error saving medicine. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!embedded && JSON.stringify(formData) !== JSON.stringify(INITIAL_FORM_DATA) && !isEditing) {
            if (window.confirm('Are you sure you want to close? Unsaved changes will be lost.')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    // Si está embebido, solo renderizar el contenido del formulario
    if (embedded) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                    <Stack spacing={3}>
                        {/* Imagen */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Product Image
                            </Typography>
                            
                            {formData.imageUrl ? (
                                <Card sx={{ maxWidth: 200, mb: 2 }}>
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={formData.imageUrl}
                                        alt="Preview"
                                    />
                                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={removeImage}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                </Card>
                            ) : null}
                            
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadIcon />}
                                fullWidth
                            >
                                {formData.imageUrl ? 'Change image' : 'Upload image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                />
                            </Button>
                        </Box>

                        <Divider />

                        {/* Información básica */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Basic Information
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Name *"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />

                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={3}
                                />

                                <TextField
                                    fullWidth
                                    label="Category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Cost"
                                            name="cost"
                                            value={formData.cost}
                                            onChange={handleChange}
                                            type="number"
                                            error={!!errors.cost}
                                            helperText={errors.cost}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Price *"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                            type="number"
                                            error={!!errors.price}
                                            helperText={errors.price}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    label="Stock *"
                                    name="stock"
                                    value={formData.stock}
                                    onChange={handleChange}
                                    type="number"
                                    error={!!errors.stock}
                                    helperText={errors.stock}
                                />
                            </Stack>
                        </Box>

                        <Divider />

                        {/* Especificaciones */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Specifications
                            </Typography>
                            <Stack spacing={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit of Measure</InputLabel>
                                            <Select
                                                label="Unit of Measure"
                                                name="uom"
                                                value={formData.uom}
                                                onChange={handleChange}
                                            >
                                                {UOM_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                label="Status"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                {STATUS_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    label="Dosage"
                                    name="dosage"
                                    value={formData.dosage}
                                    onChange={handleChange}
                                    placeholder="e.g. 500mg, twice daily"
                                />

                                <TextField
                                    fullWidth
                                    label="Expiry Date"
                                    name="expiryDate"
                                    value={formData.expiryDate}
                                    onChange={handleChange}
                                    type="date"
                                    error={!!errors.expiryDate}
                                    helperText={errors.expiryDate}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                {/* Footer */}
                <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            startIcon={<CancelIcon />}
                            fullWidth
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                            fullWidth
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
                        </Button>
                    </Stack>
                </Box>

                {/* Notificaciones */}
                <Snackbar
                    open={notification.open}
                    autoHideDuration={4000}
                    onClose={() => setNotification({ ...notification, open: false })}
                >
                    <Alert
                        severity={notification.severity}
                        onClose={() => setNotification({ ...notification, open: false })}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    // Renderizado normal (no embebido)
    return (
        <>
            <Drawer anchor="right" open={open} onClose={handleClose} sx={{ zIndex: 1300 }}>
                <Box sx={{ width: 450, height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {isEditing ? 'Edit Medicine' : 'New Medicine'}
                            </Typography>
                            <IconButton onClick={handleClose} size="large">
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                        {isEditing && (
                            <Chip 
                                label={`ID: ${selectedMedicine?._id || selectedMedicine?.id}`} 
                                size="small" 
                                variant="outlined" 
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Box>

                    {/* Content - Reusa el mismo contenido del modo embebido */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                        <Stack spacing={3}>
                            {/* ... mismo contenido que en el modo embebido ... */}
                            {/* Copiar todo el contenido del Stack desde el modo embebido */}
                        </Stack>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                startIcon={<CancelIcon />}
                                fullWidth
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                startIcon={<SaveIcon />}
                                fullWidth
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Drawer>

            {/* Notificaciones */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert
                    severity={notification.severity}
                    onClose={() => setNotification({ ...notification, open: false })}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddMedicineDrawer;